import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import moment from "moment";
import { TimelineChartData } from "@/models/timeline-chart-data";
import { CategoryChartData } from "@/models/category-chart-data";

@Module({ name: "InvoiceVolumeAnalyticsStore", namespaced: true })
class InvoiceVolumeAnalyticsStore extends VuexModule {
  private dateRange: { [key: string]: string } = {
    startDate: moment()
      .subtract(1, "weeks")
      .format()
      .toString()
      .substring(0, 10),
    endDate: moment()
      .format()
      .toString()
      .substring(0, 10)
  };
  private selectedSite = "";
  private searchButtonClicked = 0;
  private invoiceVolumeByTabData: CategoryChartData[] = [];
  private invoiceVolumeByProcessorData: CategoryChartData[] = [];
  private invoiceVolumeBySupplierData: CategoryChartData[] = [];
  private invoiceVolumeByCountryData: CategoryChartData[] = [];
  private invoiceVolumeHistoricalData: TimelineChartData[] = [];
  private numOfInvoicesReceived = 0;
  private numOfSuppliers = 0;
  private numOfCountries = 0;
  private numOfInvoiceCategories = 0;
  private numOfApProcessors = 0;
  private autoAddDataSnapshot = "";
  private autoAddSetting = "";
  private autoAddInvoiceData: CategoryChartData[] = [];
  private autoAddSampleSizeData: CategoryChartData[] = [];

  @Mutation
  private setDateRange(dateRange: { [key: string]: string }) {
    this.dateRange = dateRange;
  }

  @Mutation
  private setSearchButtonClicked() {
    this.searchButtonClicked += 1;
  }

  @Mutation
  private setSelectedSite(site: string) {
    this.selectedSite = site;
  }

  @Mutation
  private setInvoiceVolumeByTabData(data: CategoryChartData[]) {
    this.invoiceVolumeByTabData = data;
  }

  @Mutation
  private setInvoiceVolumeBySupplierData(data: CategoryChartData[]) {
    this.invoiceVolumeBySupplierData = data;
  }

  @Mutation
  private setInvoiceVolumeByProcessorData(data: CategoryChartData[]) {
    this.invoiceVolumeByProcessorData = data;
  }

  @Mutation
  private setInvoiceVolumeByCountryData(data: CategoryChartData[]) {
    this.invoiceVolumeByCountryData = data;
  }

  @Mutation
  private setInvoiceVolumeHistoricalData(data: TimelineChartData[]) {
    this.invoiceVolumeHistoricalData = data;
  }

  @Mutation
  private setNumberOfInvoicesReceived(numOfInvoices: number) {
    this.numOfInvoicesReceived = numOfInvoices;
  }

  @Mutation
  private setNumberOfSuppliers(numOfSuppliers: number) {
    this.numOfSuppliers = numOfSuppliers;
  }

  @Mutation
  private setNumberOfCountries(numOfCountries: number) {
    this.numOfCountries = numOfCountries;
  }

  @Mutation
  private setNumberOfInvoiceCategories(numOfApPortalTabs: number) {
    this.numOfInvoiceCategories = numOfApPortalTabs;
  }

  @Mutation
  private setNumberOfApProcessors(numOfApProcessors: number) {
    this.numOfApProcessors = numOfApProcessors;
  }

  @Mutation
  private setAutoAddDataSnapshot(data: string) {
    this.autoAddDataSnapshot = data;
  }

  @Mutation
  private setAutoAddInvoiceData(data: CategoryChartData[]) {
    this.autoAddInvoiceData = data;
  }

  @Mutation
  private setAutoAddSampleSizeData(data: CategoryChartData[]) {
    this.autoAddSampleSizeData = data;
  }

  @Mutation
  private setAutoAddSetting(setting: string) {
    this.autoAddSetting = setting;
  }

  @Action({ rawError: true })
  public updateDateRange(dateRange: { [key: string]: string }) {
    this.context.commit("setDateRange", dateRange);
  }

  @Action({ rawError: true })
  public clickSearchButton() {
    this.context.commit("setSearchButtonClicked");
  }

  @Action({ rawError: true })
  public updateSelectedSite(site: string) {
    this.context.commit("setSelectedSite", site);
  }

  @Action({ rawError: true })
  public updateInvoiceVolumeByTabData(data: CategoryChartData[]) {
    this.context.commit("setInvoiceVolumeByTabData", data);
  }

  @Action({ rawError: true })
  public updateInvoiceVolumeByProcessorData(data: CategoryChartData[]) {
    this.context.commit("setInvoiceVolumeByProcessorData", data);
  }

  @Action({ rawError: true })
  public updateInvoiceVolumeBySupplierData(data: CategoryChartData[]) {
    this.context.commit("setInvoiceVolumeBySupplierData", data);
  }

  @Action({ rawError: true })
  public updateInvoiceVolumeByCountryData(data: CategoryChartData[]) {
    this.context.commit("setInvoiceVolumeByCountryData", data);
  }

  @Action({ rawError: true })
  public updateInvoiceVolumeHistoricalData(data: TimelineChartData[]) {
    this.context.commit("setInvoiceVolumeHistoricalData", data);
  }

  @Action({ rawError: true })
  public updateNumberOfSuppliers(numOfSuppliers: number) {
    this.context.commit("setNumberOfSuppliers", numOfSuppliers);
  }

  @Action({ rawError: true })
  public updateNumberOfCountries(numOfCountries: number) {
    this.context.commit("setNumberOfCountries", numOfCountries);
  }

  @Action({ rawError: true })
  public updateNumberOfInvoiceCategories(numOfApPortalTabs: number) {
    this.context.commit("setNumberOfInvoiceCategories", numOfApPortalTabs);
  }

  @Action({ rawError: true })
  public updateNumberOfApProcessors(numOfApProcessors: number) {
    this.context.commit("setNumberOfApProcessors", numOfApProcessors);
  }

  @Action({ rawError: true })
  public updateNumberOfInvoicesReceived(numOfInvoicesReceived: number) {
    this.context.commit("setNumberOfInvoicesReceived", numOfInvoicesReceived);
  }

  @Action({ rawError: true })
  public updateAutoAddDataSnapshot(data: string) {
    this.context.commit("setAutoAddDataSnapshot", data);
  }

  @Action({ rawError: true })
  public updateAutoAddInvoiceData(data: CategoryChartData[]) {
    this.context.commit("setAutoAddInvoiceData", data);
  }

  @Action({ rawError: true })
  public updateAutoAddSampleSizeData(data: CategoryChartData[]) {
    this.context.commit("setAutoAddSampleSizeData", data);
  }

  @Action({ rawError: true })
  public updateAutoAddSetting(setting: string) {
    this.context.commit("setAutoAddSetting", setting);
  }

  public get getDateRange(): { [key: string]: string } {
    return this.dateRange;
  }

  public get getSearchButtonClicked(): number {
    return this.searchButtonClicked;
  }

  public get getSelectedSite(): string {
    return this.selectedSite;
  }

  public get getInvoiceVolumeBySupplierData(): CategoryChartData[] {
    return this.invoiceVolumeBySupplierData;
  }

  public get getInvoiceVolumeByCountryData(): CategoryChartData[] {
    return this.invoiceVolumeByCountryData;
  }

  public get getInvoiceVolumeByTabData(): CategoryChartData[] {
    return this.invoiceVolumeByTabData;
  }

  public get getInvoiceVolumeByProcessorData(): CategoryChartData[] {
    return this.invoiceVolumeByProcessorData;
  }

  public get getInvoiceVolumeHistoricalData(): TimelineChartData[] {
    return this.invoiceVolumeHistoricalData;
  }

  public get getNumOfSuppliers(): number {
    return this.numOfSuppliers;
  }

  public get getNumOfCountries(): number {
    return this.numOfCountries;
  }

  public get getNumOfInvoicesReceived(): number {
    return this.numOfInvoicesReceived;
  }

  public get getNumOfInvoiceCategories(): number {
    return this.numOfInvoiceCategories;
  }

  public get getNumOfApProcessors(): number {
    return this.numOfApProcessors;
  }

  public get getAutoAddDataSnapshot(): string {
    return this.autoAddDataSnapshot;
  }

  public get getAutoAddInvoiceData(): CategoryChartData[] {
    return this.autoAddInvoiceData;
  }

  public get getAutoAddSampleSizeData(): CategoryChartData[] {
    return this.autoAddSampleSizeData;
  }

  public get getAutoAddSetting(): string {
    return this.autoAddSetting;
  }
}

export default InvoiceVolumeAnalyticsStore;
